import { action, computed, observable, reaction, makeObservable } from 'mobx'
import { createTransformer } from 'mobx-utils'
import { task } from 'mobx-task'
import last from 'lodash/last'
import flatMap from 'lodash/flatMap'
import JobViewModel from '../../JobViewModel'
import { Milestone, Task } from 'jobs/Job'
import MilestoneStore from 'jobs/MilestoneStore'
import { TaskDesignee } from 'jobs/dtos/job'

/**
 * View model for the milestones view.
 */
export default class MilestonesViewModel {
  completionModal = new MilestoneCompletionModal(this)
  taskLists = this._createTaskLists()

  @observable selectedMilestoneId: string | null = null
  @observable showingMilestoneSwitcher = false

  constructor(
    public jobVM: JobViewModel,
    private milestoneStore: MilestoneStore
  ) {
    makeObservable(this)
    const initial = this.nextMilestone || last(this.milestones)
    this.selectedMilestoneId = initial ? initial.id : null
    this._createTaskLists()

    reaction(
      () => this.milestones.some((m) => m.id === this.selectedMilestoneId),
      (included) =>
        !included &&
        this.selectMilestone(this.nextMilestone || last(this.milestones))
    )
  }

  /**
   * Returns the currently selected milestone.
   * If none are selected (change in milestones for example), returns the
   * next milestone, or the last one if all milestones have been completed.
   */
  @computed
  get milestone() {
    if (!this.selectedMilestoneId) {
      return this.nextMilestone || last(this.milestones)
    }

    return (
      this.jobVM.job.milestones.find(
        (x) => x.id === this.selectedMilestoneId
      ) ||
      this.nextMilestone ||
      last(this.milestones)
    )
  }

  /**
   * Returns the job member representing the current user for the job.
   * Returns `null` if the current user is not a collaborator (could be a HQ person).
   */
  @computed
  get collaborator() {
    return this.jobVM.currentUserCollaborator || null
  }

  @computed
  get milestones() {
    return this.jobVM.job.milestones
  }

  @computed
  get completedMilestones() {
    return this.milestones.filter((x) => x.completed)
  }

  @computed
  get allTasksForCurrentMilestone() {
    return flatMap(this.taskLists, (x) => x.tasks.map((x) => x.taskItem))
  }

  @computed
  get tasksCount() {
    return this.allTasksForCurrentMilestone.length
  }

  @computed
  get completedTasksCount() {
    return this.allTasksForCurrentMilestone.filter((x) => x.completed).length
  }

  @computed
  get isProvider() {
    const member = this.collaborator
    return member ? member.type === 'PROVIDER' : false
  }

  @computed
  get nextMilestone() {
    return this.milestones.find(
      (m) => !areDesigneeTasksCompleted(m, this.designee)
    )
  }

  @computed
  get previousMilestone() {
    const index = this.milestone
      ? Math.max(0, this.milestones.indexOf(this.milestone)) - 1
      : 0
    return this.milestones[index]
  }

  @computed
  get canCompleteMilestone() {
    if (!this.nextMilestone) {
      return true
    }

    return (
      // if you are a pro you can always complete them
      this.isProvider ||
      // otherwise check
      (this.milestone ? this.milestones.indexOf(this.milestone) : -1) <=
        this.milestones.indexOf(this.nextMilestone)
    )
  }

  @computed
  get taskCompletionProgress() {
    return Math.round(
      (this.completedTasksCount / Math.max(this.tasksCount, 1)) * 100
    )
  }

  @computed
  get designee() {
    return this.collaborator?.type === 'PROVIDER' ? 'Provider' : 'Client'
  }

  @action.bound
  showMilestoneSwitcher() {
    this.showingMilestoneSwitcher = true
  }

  @action.bound
  closeMilestoneSwitcher() {
    this.showingMilestoneSwitcher = false
  }

  @action.bound
  selectMilestone(milestone: Milestone | undefined) {
    if (!milestone) {
      return
    }
    this.selectedMilestoneId = milestone.id
    this.closeMilestoneSwitcher()
  }

  /**
   * Creates the task lists.
   */
  _createTaskLists() {
    // Collaborator = somebody working on the job
    const isCollaborator = !!this.collaborator
    const isProvider = this.isProvider
    return [
      new TaskListViewModel(
        isCollaborator ? 'My Tasks' : 'Client Tasks',
        isProvider ? 'Provider' : 'Client',
        true,
        isCollaborator,
        this,
        this.milestoneStore
      ),
      new TaskListViewModel(
        isCollaborator
          ? isProvider
            ? 'Client Tasks'
            : 'Provider Tasks'
          : 'Provider Tasks',
        isProvider ? 'Client' : 'Provider',
        // If you are not on the job, then you are likely viewing from HQ, so
        // expand this tasklist as well.
        !isCollaborator,
        false,
        this,
        this.milestoneStore
      ),
    ]
  }
}

/**
 * Task list state.
 */
export class TaskListViewModel {
  @observable
  expanded = false

  transform = createTransformer(
    (t: Task) =>
      new TaskListItemViewModel(
        this.milestonesVM,
        this,
        t,
        this.isDesignee,
        this.milestoneStore
      )
  )

  constructor(
    public title: string,
    public designee: TaskDesignee,
    expanded: boolean,
    public isDesignee: boolean,
    private milestonesVM: MilestonesViewModel,
    private milestoneStore: MilestoneStore
  ) {
    makeObservable(this)
    this.expanded = expanded
  }

  @computed
  get milestone() {
    return this.milestonesVM.milestone
  }

  @computed
  get count() {
    return this.tasks.length
  }

  @computed
  get completedCount() {
    return this.tasks.filter((t) => t.taskItem.completed).length
  }

  @computed
  get tasks() {
    if (!this.milestone) {
      return []
    }

    const designee = this.designee
    return this.milestone.tasks
      .filter((x) => x.designee === designee)
      .map(this.transform)
  }

  @computed
  get pendingTasks() {
    return this.tasks.filter((t) => !t.taskItem.completed)
  }

  @action.bound
  toggleExpansion() {
    this.expanded = !this.expanded
  }
}

/**
 * Task list item state.
 */
export class TaskListItemViewModel {
  constructor(
    private milestonesVM: MilestonesViewModel,
    public list: TaskListViewModel,
    public taskItem: Task,
    public isDesignee: boolean,
    private milestoneStore: MilestoneStore
  ) {
    makeObservable(this)
    this.toggleCompletion = this.toggleCompletion.bind(this)
  }

  @computed
  get canComplete() {
    // if you are a team member with UPDATE_TEAM_JOBS permission you can complete them too
    if (
      this.list.designee === 'Client' &&
      this.milestonesVM.jobVM.currentTeamMemberOfOwnerTeam?.hasPermission(
        'UPDATE_TEAM_JOBS'
      )
    ) {
      return true
    }

    return (
      this.isDesignee &&
      this.milestonesVM.canCompleteMilestone &&
      this.milestonesVM.jobVM.job.status !== 'CLOSED'
    )
  }

  @computed
  get isNextIncompleteTask() {
    const nextTask = this.list.tasks.find((x) => !x.taskItem.completed)
    return this === nextTask
  }

  @computed
  get isLastInList() {
    return this.list.tasks.indexOf(this) === this.list.tasks.length - 1
  }

  toggleCompletion = task.resolved(() => {
    if (
      !this.isDesignee &&
      !this.milestonesVM.jobVM.currentTeamMemberOfOwnerTeam?.hasPermission(
        'UPDATE_TEAM_JOBS'
      )
    ) {
      return
    }

    return this.taskItem.completed
      ? this.milestoneStore.uncomplete(this.taskItem)
      : this.milestoneStore.complete(this.taskItem)
  })
}

/**
 * State for the completion modal.
 */
class MilestoneCompletionModal {
  @observable showing = false
  @observable milestone: Milestone | null = null

  constructor(private milestonesVM: MilestonesViewModel) {
    makeObservable(this)
  }

  deactivate = onMilestoneCompleted(
    this.milestonesVM,
    this.onMilestoneCompleted.bind(this)
  )

  @computed
  get allMilestonesCompleted() {
    return this.milestonesVM.milestones.every((x) =>
      areDesigneeTasksCompleted(x, this.milestonesVM.designee)
    )
  }

  @action.bound
  show(milestone: Milestone) {
    this.showing = true
    this.milestone = milestone
  }

  @action.bound
  hide() {
    this.showing = false
  }

  @action.bound
  proceed() {
    this.hide()
    if (this.milestonesVM.nextMilestone) {
      this.milestonesVM.selectMilestone(this.milestonesVM.nextMilestone)
    }
  }

  /**
   * Triggered when a new milestone is seen as completed. Only once per milestone (*does not persist between reloads!*)
   *
   * @param {*} milestone
   */
  @action.bound
  onMilestoneCompleted(milestone: Milestone) {
    // at this point all task on the job have been completed, so we sort the tasks
    // and fetch the latest completed task to check if the current user is the
    // one who completed it in order to show the modal
    const lastCompleted = milestone.tasks
      .slice()
      .sort((a, b) => b.completedAt.getTime() - a.completedAt.getTime())[0]

    // Only show to who completed the task.
    if (this.milestonesVM.collaborator?.member === lastCompleted.completedBy) {
      this.show(milestone)
    }
  }
}

/**
 * Calls `callback` when a milestone is observed to have been just completed.
 *
 * @param {*} milestonesVM
 * @param {*} callback
 * @returns disposer function
 */
function onMilestoneCompleted(
  milestonesVM: MilestonesViewModel,
  callback: (milestone: Milestone) => unknown
) {
  const getLastCompletedMilestoneId = () => {
    const found = milestonesVM.milestones
      .filter((x) => x.tasks.some((t) => t.designee === milestonesVM.designee))
      .slice()
      .reverse()
      .find((x) => areDesigneeTasksCompleted(x, milestonesVM.designee))
    return found ? found.id : null
  }
  let lastCompletedMilestoneId = getLastCompletedMilestoneId()
  return reaction(
    () => {
      const milestoneIds = milestonesVM.milestones.map((x) => x.id)
      // This function returns a Milestone that was just completed during this run.
      const nextId = getLastCompletedMilestoneId()
      if (!nextId) {
        return null
      }
      if (nextId === lastCompletedMilestoneId) {
        return null
      }
      const ret =
        milestoneIds.indexOf(lastCompletedMilestoneId!) <
        milestoneIds.indexOf(nextId)
          ? milestonesVM.milestones.find((x) => x.id === nextId)
          : null

      if (ret) {
        lastCompletedMilestoneId = nextId
      }

      return ret
    },
    (milestone) => milestone && callback(milestone)
  )
}

/**
 * Whether or not the user has completed their tasks at the given milestone
 * @param milestone
 * @param designee
 * @returns
 */
function areDesigneeTasksCompleted(
  milestone: Milestone,
  designee: TaskDesignee
) {
  return !milestone.tasks.some((t) => t.designee === designee && !t.completed)
}
